/**
 * Portal types (portal_types): online_school, jamia, hifz_ul_quran, etc.
 * Associate, student, and teacher member_init payloads expose `portal_types` on the root
 * object. Other roles use linked affiliate first, then top-level.
 * Legacy `associate_types` / Prisma `portalTypes` are still read when present.
 */
function pickPortalTypes(obj) {
  if (!obj) return [];
  if (Array.isArray(obj.portal_types)) return obj.portal_types;
  if (Array.isArray(obj.portalTypes)) return obj.portalTypes;
  if (Array.isArray(obj.associate_types)) return obj.associate_types;
  return [];
}

export function getAssociateTypes(userInfo) {
  if (!userInfo) return [];
  const ut = userInfo.user_type;

  if (ut === "associate" || ut === "student" || ut === "teacher") {
    return pickPortalTypes(userInfo);
  }

  const nested = pickPortalTypes(userInfo.associate);
  if (nested.length > 0) return nested;
  return pickPortalTypes(userInfo);
}

export function isOnlineSchoolCapability(userInfo) {
  return getAssociateTypes(userInfo).includes("online_school");
}

export function teacherHasGlobalStudentsListAccess(userInfo) {
  if (!userInfo || userInfo.user_type !== "teacher") return true;
  return isOnlineSchoolCapability(userInfo);
}

/** Student reports (portal + API): online school or Hifz ul Quran on the affiliate / member_init types. */
export function isStudentReportsCapability(userInfo) {
  const types = getAssociateTypes(userInfo);
  return types.includes("online_school") || types.includes("hifz_ul_quran");
}

export function isHifzUlQuranCapability(userInfo) {
  if (!userInfo) return false;
  return getAssociateTypes(userInfo).includes("hifz_ul_quran");
}

export const HIFZ_SESSION_TYPE_OPTIONS = [
  { value: "lesson", labelKey: "hifz.sessionType.lesson" },
  { value: "revision_1", labelKey: "hifz.sessionType.revision1" },
  { value: "revision_2", labelKey: "hifz.sessionType.revision2" },
];

/**
 * @param {string} value
 * @param {(key: string) => string} t - i18next `t`
 */
export function formatHifzSessionTypeLabel(value, t) {
  if (value == null || value === "") return t("hifz.dash");
  const opt = HIFZ_SESSION_TYPE_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.labelKey) : String(value);
}

export const HIFZ_LESSON_RATING_OPTIONS = [
  { value: 1, labelKey: "hifz.rating.1" },
  { value: 2, labelKey: "hifz.rating.2" },
  { value: 3, labelKey: "hifz.rating.3" },
  { value: 4, labelKey: "hifz.rating.4" },
  { value: 5, labelKey: "hifz.rating.5" },
];

/** @param {(key: string) => string} t */
export function formatHifzLessonRatingLabel(value, t) {
  if (value == null || value === "") return t("hifz.dash");
  const n = Number(value);
  if (Number.isNaN(n)) return t("hifz.dash");
  const opt = HIFZ_LESSON_RATING_OPTIONS.find((o) => o.value === n);
  return opt ? t(opt.labelKey) : String(n);
}

export function usesHifzClassScopedStudentLessons(userInfo) {
  return isHifzUlQuranCapability(userInfo) && isStudentReportsCapability(userInfo);
}

/** i18n key for sidebar: student lessons vs students reports. */
export function getStudentProgressNavTitleKey(userInfo) {
  return isHifzUlQuranCapability(userInfo) ? "sidebar.studentLessons" : "sidebar.studentsReports";
}

/** i18n key for list page h2. */
export function getStudentProgressListHeadingKey(userInfo) {
  return isHifzUlQuranCapability(userInfo) ? "heading.studentLessonsList" : "heading.studentReportsList";
}

export function getStudentProgressRecordNounTitleKey(userInfo) {
  return isHifzUlQuranCapability(userInfo) ? "progress.nounTitle.lesson" : "progress.nounTitle.report";
}

export function getStudentProgressRecordNounLowerKey(userInfo) {
  return isHifzUlQuranCapability(userInfo) ? "progress.nounLower.lesson" : "progress.nounLower.report";
}

export function isJamiaCapability(userInfo) {
  return getAssociateTypes(userInfo).includes("jamia");
}

export function programTypesForClassPeriodsApi(userInfo) {
  const out = [];
  if (isJamiaCapability(userInfo)) out.push("jamia");
  if (isHifzUlQuranCapability(userInfo)) out.push("hifz");
  return out;
}

export function isJamiaOrHifzUlQuranCapability(member) {
  const types = getAssociateTypes(member);
  return types.includes("jamia") || types.includes("hifz_ul_quran");
}

export function getAssociateTypesFromAssociate(associate) {
  return pickPortalTypes(associate);
}

export function getPortalTypesFromMember(member) {
  if (!member) return [];
  const fromArrays = getAssociateTypesFromAssociate(member);
  if (fromArrays.length > 0) {
    return fromArrays.map((t) => String(t).trim()).filter(Boolean);
  }
  const raw = member.portal_types ?? member.portalTypes;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((t) => String(t).trim()).filter(Boolean);
        }
      } catch {
        /* ignore */
      }
    }
    if (trimmed) return [trimmed];
  }
  return [];
}


/**
 * Checks whether the current user has access to the Certifications feature.
 * Gated to hafizaslam@xinomail.com and linked users during private beta.
 */
export function isCertificatesCapability(userInfo) {
  if (!userInfo) return false;
  if (typeof userInfo.can_access_certificates === 'boolean') {
    return userInfo.can_access_certificates;
  }
  const allowedEmails = (
    process.env.REACT_APP_CERTIFICATES_ALLOWED_EMAILS ||
    'hafizaslam@xinomail.com'
  )
    .toLowerCase()
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (allowedEmails.includes('*')) return true;

  const userEmail = (userInfo.email || '').toLowerCase().trim();
  if (userEmail && allowedEmails.includes(userEmail)) return true;

  const associateEmail = (
    userInfo.associate?.email ||
    userInfo.associate_email ||
    ''
  )
    .toLowerCase()
    .trim();
  if (associateEmail && allowedEmails.includes(associateEmail)) return true;

  return false;
}
